import type { ConversationType, ItemRow } from 'owa-graph-schema';
import { getIsPinnedTimestamp } from 'owa-mail-list-store';
import { owaDate, startOfDay, addDays, getDay, getTimestamp } from 'owa-datetime';

/**
 * Gets the priority view lookback cutoff as a UTC epoch timestamp (ms).
 * On Mondays, looks back to Friday; otherwise looks back to yesterday.
 * All comparisons use UTC so there is no local-time / DST ambiguity.
 */
function getPriorityViewCutoffTimestamp(): number {
    const now = owaDate('UTC');
    const today = startOfDay(now);
    const isMonday = getDay(now) === 1;
    const lookbackDays = isMonday ? 3 : 1;
    const cutoff = addDays(today, -lookbackDays);
    return getTimestamp(cutoff);
}

/**
 * Parses a server ISO timestamp to a UTC epoch (ms).
 * Server timestamps are ISO 8601 / UTC, so parsing via owaDate('UTC', ...) is correct.
 */
function toUtcTimestamp(iso: string): number {
    return getTimestamp(owaDate('UTC', iso));
}

/**
 * Filters item rows to only include items within the priority lookback window.
 * Uses ReceivedOrRenewTime so that snoozed (re-delivered) items are included.
 * For pinned items (whose ReceivedOrRenewTime is a far-future sentinel), falls
 * back to DateTimeReceived so that old pinned mail is not incorrectly included.
 * On Mondays, includes Friday through Monday; otherwise includes today and yesterday.
 * @param rows - Array of ItemRow to filter
 * @returns Filtered array containing only recent items
 */
export function filterItemRowsForPriorityView(rows: ItemRow[]): ItemRow[] {
    const cutoff = getPriorityViewCutoffTimestamp();

    return rows.filter(row => {
        const renewTime = row.ReceivedOrRenewTime;
        if (!renewTime) {
            return false;
        }

        // Pinned items have a far-future sentinel in ReceivedOrRenewTime (year 4500).
        // Fall back to the original received date for the lookback comparison.
        if (getIsPinnedTimestamp(renewTime)) {
            if (!row.DateTimeReceived) {
                return false;
            }
            return toUtcTimestamp(row.DateTimeReceived) >= cutoff;
        }

        return toUtcTimestamp(renewTime) >= cutoff;
    });
}

/**
 * Filters conversation rows to only include conversations within the priority lookback window.
 * Uses LastDeliveryOrRenewTime so that snoozed (re-delivered) conversations are included.
 * For pinned conversations (whose LastDeliveryOrRenewTime is a far-future sentinel), falls
 * back to LastDeliveryTime so that old pinned conversations are not incorrectly included.
 * On Mondays, includes Friday through Monday; otherwise includes today and yesterday.
 * @param rows - Array of ConversationType to filter
 * @returns Filtered array containing only recent conversations
 */
export function filterConversationRowsForPriorityView(
    rows: ConversationType[]
): ConversationType[] {
    const cutoff = getPriorityViewCutoffTimestamp();

    return rows.filter(row => {
        const renewTime = row.LastDeliveryOrRenewTime;
        if (!renewTime) {
            return false;
        }

        // Pinned conversations have a far-future sentinel in LastDeliveryOrRenewTime (year 4500).
        // Fall back to the original delivery date for the lookback comparison.
        if (getIsPinnedTimestamp(renewTime)) {
            if (!row.LastDeliveryTime) {
                return false;
            }
            return toUtcTimestamp(row.LastDeliveryTime) >= cutoff;
        }

        return toUtcTimestamp(renewTime) >= cutoff;
    });
}
